import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { FaChevronCircleLeft, FaCoins } from "react-icons/fa";

const PurchaseHistoryDetails = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [user, setUser] = useState(null);
    const [availableCoins, setAvailableCoins] = useState(0);
    const [spentCoins, setSpentCoins] = useState(0);
    const [sendGiftCoins, setSendGiftCoins] = useState(0);
    const [loadingCoins, setLoadingCoins] = useState(true);

    const limit = 10;

    useEffect(() => {
        fetchHistory(pagination.currentPage);
        fetchUser();
    }, [pagination.currentPage]);

    useEffect(() => {
        if (user?._id) {
            fetchAvailableCoins();
            fetchSpentCoins();
        }
    }, [user?._id, user?.gender]);

    const fetchUser = async () => {
        try {
            const res = await axios.get(`/api/v1/admin/users/${userId}`);
            if (res.data) {
                setUser(res.data.data || res.data);
            }
        } catch (error) {
            console.error("User fetch error:", error);
        }
    };

    const fetchAvailableCoins = async () => {
        if (!userId) return;
        try {
            setLoadingCoins(true);
            // Use the same API as UserDetailsModal
            // For female users, use withdrawal stats API, otherwise use history stats API
            const userGender = user?.gender?.toLowerCase();
            const apiEndpoint = userGender === "female"
                ? `/api/v1/app/coin-earnings/withdrawal/stats/${userId}`
                : `/api/v1/app/coin-earnings/history/stats/${userId}`;
            
            const res = await axios.get(apiEndpoint);
            const data = res.data || {};
            setAvailableCoins(data.availableCoins || 0);
        } catch (error) {
            console.error("Error fetching available coins:", error);
            setAvailableCoins(0);
        } finally {
            setLoadingCoins(false);
        }
    };

    const fetchSpentCoins = async () => {
        if (!userId) return;
        try {
            // Spent coins and send gift coins come from history stats API (same for all users)
            const res = await axios.get(
                `/api/v1/app/coin-earnings/history/stats/${userId}`
            );
            const data = res.data || {};
            setSpentCoins(data.spentCoins || 0);
            setSendGiftCoins(data.sendGiftCoins || 0);
        } catch (error) {
            console.error("Error fetching coin stats:", error);
            setSpentCoins(0);
            setSendGiftCoins(0);
        }
    };

    const fetchHistory = async (page = 1) => {
        try {
            setLoading(true);

            const res = await axios.get(
                `/api/v1/app/payments/gethistory/${userId}`,
                { params: { page, limit } }
            );

            if (res.data.status === "OK") {
                setTransactions(res.data.data.transactions);
                setSummary(res.data.data.summary);
                setPagination(res.data.data.pagination);
            }
        } catch (error) {
            console.error("Purchase history fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (n) => n?.toLocaleString("en-IN") || "0";

    return (
        <div className="p-4 md:p-6">

            {/* BACK BUTTON */}
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-[var(--yellow)] mb-6 hover:opacity-80"
            >
                <FaChevronCircleLeft className="text-2xl" /> Back
            </button>

            {/* HEADER */}
            <div className="p-6 rounded-xl shadow mb-6 bg-[var(--black)] border border-[var(--yellow)]">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[var(--yellow)]">
                            User Purchase History
                        </h1>
                        <p className="text-[var(--yellow)] opacity-80 mt-2">
                            Detailed list of all purchase transactions
                        </p>
                    </div>

                    {/* User Details */}
                    {user && (
                        <div className="flex flex-col md:items-end gap-2">
                            <div className="text-[var(--yellow)]">
                                <p className="text-lg font-semibold">{user.fullName || user.name || "N/A"}</p>
                                <p className="text-sm opacity-80">{user.phoneNumber || user.phone || "—"}</p>
                                {user.gender && (
                                    <p className="text-sm opacity-70 capitalize">Gender: {user.gender}</p>
                                )}
                                {user.email && (
                                    <p className="text-sm opacity-70">{user.email}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* SUMMARY CARDS */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">

                    <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">
                        <p className="text-sm text-[var(--yellow)]">Total Purchased Coins</p>
                        <p className="text-2xl font-bold text-[var(--yellow)]">
                            {formatNumber(summary.totalCoinsPurchased)} coins
                        </p>
                    </div>

                    <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">
                        <p className="text-sm text-[var(--yellow)]">Total Transactions</p>
                        <p className="text-2xl font-bold text-[var(--yellow)]">
                            {summary.totalTransactions}
                        </p>
                    </div>

                    <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">
                        <p className="text-sm text-[var(--yellow)]">Total Money Spent</p>
                        <p className="text-2xl font-bold text-[var(--yellow)]">
                            ₹{formatNumber(summary.totalMoneySpent)}
                        </p>
                    </div>

                    <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">
                        <p className="text-sm text-[var(--yellow)]">Spent Coins</p>
                        {loadingCoins ? (
                            <div className="flex justify-center py-2">
                                <div className="w-5 h-5 border-2 border-[var(--yellow)] border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <p className="text-2xl font-bold text-[var(--yellow)]">
                                {formatNumber(spentCoins)} coins
                            </p>
                        )}
                    </div>

                    <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">
                        <p className="text-sm text-[var(--yellow)]">Available Coins</p>
                        {loadingCoins ? (
                            <div className="flex justify-center py-2">
                                <div className="w-5 h-5 border-2 border-[var(--yellow)] border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <p className="text-2xl font-bold text-[var(--yellow)]">
                                {formatNumber(availableCoins)} coins
                            </p>
                        )}
                    </div>

                    <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">
                        <p className="text-sm text-[var(--yellow)]">Send Gifts</p>
                        {loadingCoins ? (
                            <div className="flex justify-center py-2">
                                <div className="w-5 h-5 border-2 border-[var(--yellow)] border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <p className="text-2xl font-bold text-[var(--yellow)]">
                                {formatNumber(sendGiftCoins)} coins
                            </p>
                        )}
                    </div>

                </div>
            )}

            {/* TRANSACTION LIST */}
            <div className="bg-[var(--black)] p-6 rounded-xl shadow border border-[var(--yellow)]">

                <h2 className="text-xl font-bold text-[var(--yellow)] mb-4">
                    Transactions
                </h2>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="w-10 h-10 border-4 border-[var(--yellow)] border-t-[var(--black)]
                        rounded-full animate-spin" />
                    </div>
                ) : transactions.length === 0 ? (
                    <p className="text-center text-gray-400 py-6">No transactions found</p>
                ) : (
                    transactions.map((tx) => (
                        <div
                            key={tx._id}
                            className="border border-[var(--yellow)] rounded-xl p-4 mb-4 shadow-sm"
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-lg text-[var(--yellow)] font-bold">
                                        {tx.packageName}
                                    </p>
                                    <p className="text-[var(--yellow)] opacity-70">
                                        {new Date(tx.createdAt).toLocaleString()}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 text-[var(--yellow)] text-xl">
                                    <FaCoins />
                                    <span className="text-2xl font-bold">{tx.coins}</span>
                                </div>
                            </div>

                            <p className="text-[var(--yellow)] mt-2">
                                Status:
                                <span className="font-bold ml-1">{tx.status}</span>
                            </p>

                            <p className="text-[var(--yellow)]">
                                Paid: ₹{formatNumber(tx.amount)}
                            </p>

                            <p className="text-[var(--yellow)]">
                                Payment ID: {tx.razorpayPaymentId}
                            </p>
                        </div>
                    ))
                )}

                {/* PAGINATION */}
                {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-center mt-8">
                        <div className="flex items-center gap-2 bg-[var(--black)] border border-[var(--yellow)]
                        px-4 py-3 rounded-xl shadow-lg">

                            {/* PREV */}
                            <button
                                disabled={pagination.currentPage === 1}
                                onClick={() =>
                                    setPagination((p) => ({ ...p, currentPage: p.currentPage - 1 }))
                                }
                                className="px-3 py-2 rounded-lg bg-[var(--yellow)] text-[var(--black)]
                                disabled:opacity-40 hover:opacity-80"
                            >
                                <FaChevronCircleLeft />
                            </button>

                            {/* PAGE NUMBERS */}
                            {[...Array(pagination.totalPages)].map((_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() =>
                                            setPagination((p) => ({ ...p, currentPage: pageNum }))
                                        }
                                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold border
                                            ${
                                                pageNum === pagination.currentPage
                                                    ? "bg-[var(--yellow)] text-[var(--black)] border-[var(--yellow)]"
                                                    : "text-[var(--yellow)] border-[var(--yellow)] hover:bg-[var(--yellow)] hover:text-[var(--black)]"
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            {/* NEXT */}
                            <button
                                disabled={pagination.currentPage === pagination.totalPages}
                                onClick={() =>
                                    setPagination((p) => ({ ...p, currentPage: p.currentPage + 1 }))
                                }
                                className="px-3 py-2 rounded-lg bg-[var(--yellow)] text-[var(--black)]
                                disabled:opacity-40 hover:opacity-80"
                            >
                                ➜
                            </button>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchaseHistoryDetails;
